// CarbonCreditRulesDoc.tsx
import React from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";

const CodeBlock: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component={Paper}
    variant="outlined"
    sx={{
      p: 2,
      bgcolor: "grey.900",
      color: "grey.100",
      fontFamily: "monospace",
      fontSize: 13,
      overflowX: "auto",
      borderRadius: 2,
    }}
  >
    <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
      <code>{children}</code>
    </pre>
  </Box>
);

const CarbonCreditRulesDoc: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={4}>
        {/* Header */}
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Carbon Credit Rule Set
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Creation → Validation → Pricing → Retirement → Correlation with
            Renewable Energy &amp; CO₂ → Optional blockchain rules.
          </Typography>
        </Box>

        {/* 1. RULES FOR CREDIT CREATION */}
        <Box>
          <Typography variant="h5" gutterBottom>
            1. RULES FOR CREDIT CREATION
          </Typography>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            1.1 One-to-One Relationship
          </Typography>
          <List dense sx={{ mb: 1.5 }}>
            <ListItem>
              <ListItemText primary="Each carbon credit represents exactly 1 metric ton (1 tCO₂e) of emissions avoided, reduced, or removed." />
            </ListItem>
            <ListItem>
              <ListItemText primary="No credit may represent fractional or variable amounts outside the registry definition." />
            </ListItem>
          </List>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            1.2 Measured, Not Estimated
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Input data for credit creation must come from{" "}
            <strong>device-measured renewable-energy production</strong>, not
            user-entered or unverifiable values.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Accepted device inputs include:
          </Typography>
          <List dense sx={{ mb: 1.5 }}>
            <ListItem>
              <ListItemText primary="kWh produced" />
            </ListItem>
            <ListItem>
              <ListItemText primary="timestamped intervals" />
            </ListItem>
            <ListItem>
              <ListItemText primary="device ID" />
            </ListItem>
            <ListItem>
              <ListItemText primary="cryptographic device signature (if blockchain-based)" />
            </ListItem>
          </List>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            1.3 Provenance &amp; Tamper-Resistance
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Every credit must store at least:
          </Typography>
          <List dense sx={{ mb: 1.5 }}>
            <ListItem>
              <ListItemText primary="originating device ID" />
            </ListItem>
            <ListItem>
              <ListItemText primary="facility or geolocation (where appropriate)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="metering interval range (start–end)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="time of measurement" />
            </ListItem>
            <ListItem>
              <ListItemText primary="verification/validation method" />
            </ListItem>
            <ListItem>
              <ListItemText primary="hash of measurement logs/files for auditability" />
            </ListItem>
          </List>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            1.4 Device Whitelisting
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Credits may only be created from{" "}
            <strong>approved renewable-energy devices</strong>, such as:
          </Typography>
          <List dense sx={{ mb: 1 }}>
            <ListItem>
              <ListItemText primary="solar inverters and smart meters" />
            </ListItem>
            <ListItem>
              <ListItemText primary="HEPEK or equivalent IoT meters" />
            </ListItem>
            <ListItem>
              <ListItemText primary="wind turbines" />
            </ListItem>
            <ListItem>
              <ListItemText primary="hydro devices" />
            </ListItem>
          </List>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Devices must pass commissioning, calibration, and identity
            verification (e.g. public key, serial number).
          </Typography>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            1.5 Double-Issuance Prevention
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Measured energy can only be used once to mint carbon credits.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Tokens or certificates should include:
          </Typography>
          <List dense sx={{ mb: 1.5 }}>
            <ListItem>
              <ListItemText primary="unique interval or measurement ID" />
            </ListItem>
            <ListItem>
              <ListItemText primary="“spent/consumed” state once used for credit creation" />
            </ListItem>
            <ListItem>
              <ListItemText primary="cryptographic proof or flags preventing replay" />
            </ListItem>
          </List>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            1.6 Automatic Conversion Formula
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The platform may define a deterministic conversion from kWh to
            tokens:
          </Typography>
          <CodeBlock>
            {`CarbonTokens = kWh × CO₂_Intensity_Factor × Quality_Factor`}
          </CodeBlock>
          <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
            Where:
          </Typography>
          <List dense sx={{ mb: 1 }}>
            <ListItem>
              <ListItemText primary="CO₂_Intensity_Factor = grams of CO₂ avoided per kWh (region-dependent)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Quality_Factor = multiplier for technology/type (solar, wind, hydro, etc.)" />
            </ListItem>
          </List>
          <Typography variant="body2">
            Final credit issuance is anchored to:
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            <strong>1 credit = 1,000 kg CO₂e = 1 metric ton.</strong>
          </Typography>
        </Box>

        <Divider />

        {/* 2. RULES FOR CREDIT VALIDATION & VERIFICATION */}
        <Box>
          <Typography variant="h5" gutterBottom>
            2. RULES FOR CREDIT VALIDATION &amp; VERIFICATION
          </Typography>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            2.1 Third-Party or Protocol-Based Validation
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Validation must follow a recognised methodology, such as ISO 14064,
            ISO 14067, Gold Standard, VCS (Verra), or a national renewable
            energy offset standard.
          </Typography>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            2.2 Real-Time Validation (IoT + Blockchain)
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Measurement logs should be validatable using:
          </Typography>
          <List dense sx={{ mb: 1.5 }}>
            <ListItem>
              <ListItemText primary="tamper-proof timestamps" />
            </ListItem>
            <ListItem>
              <ListItemText primary="device cryptographic signatures" />
            </ListItem>
            <ListItem>
              <ListItemText primary="zero-knowledge proofs where privacy is required (optional)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="chain-of-custody proofs for data" />
            </ListItem>
          </List>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            2.3 Transparency Requirements
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Each credit record should expose:
          </Typography>
          <List dense sx={{ mb: 1.5 }}>
            <ListItem>
              <ListItemText primary="source device / facility" />
            </ListItem>
            <ListItem>
              <ListItemText primary="energy type (solar, wind, hydro, etc.)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="CO₂e methodology applied" />
            </ListItem>
            <ListItem>
              <ListItemText primary="unique serial number" />
            </ListItem>
            <ListItem>
              <ListItemText primary="validation/audit hash" />
            </ListItem>
          </List>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            2.4 Registry Integrity
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Registry must prevent silent deletion of credits; any reversal requires an audit trail." />
            </ListItem>
            <ListItem>
              <ListItemText primary="All actions (mint, transfer, retire) must be traceable." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Validator roles must be permissioned and auditable." />
            </ListItem>
          </List>
        </Box>

        <Divider />

        {/* 3. RULES FOR CARBON CREDIT PRICING */}
        <Box>
          <Typography variant="h5" gutterBottom>
            3. RULES FOR CARBON CREDIT PRICING
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Pricing must be transparent, algorithmic, and grounded in CO₂e
            content plus market context.
          </Typography>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            3.1 Base Price Calculation
          </Typography>
          <CodeBlock>
            {`Base_Price = (CO2_Avoided_per_kWh × Regional_Carbon_Price)`}
          </CodeBlock>

          <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }} gutterBottom>
            3.2 Market Modifiers
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Final price per credit can apply modifiers:
          </Typography>
          <CodeBlock>
            {`Final_Price = Base_Price × Market_Modifier × Quality_Score`}
          </CodeBlock>
          <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
            Where modifiers may include:
          </Typography>
          <List dense sx={{ mb: 1.5 }}>
            <ListItem>
              <ListItemText primary="supply/demand dynamics" />
            </ListItem>
            <ListItem>
              <ListItemText primary="age of the credit" />
            </ListItem>
            <ListItem>
              <ListItemText primary="technology premiums (e.g. solar/wind bonuses)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="jurisdiction incentives or penalties" />
            </ListItem>
          </List>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            3.3 Benchmark Indexing
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Pricing should reference at least one benchmark (EU ETS, ACCU,
            CORSIA, voluntary market indices) to demonstrate market sanity.

          </Typography>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            3.4 Device Efficiency Bonus
          </Typography>
          <CodeBlock>
            {`Efficiency_Score = (Actual_kWh_Output) / (Rated_Capacity × Time)`}
          </CodeBlock>
          <Typography variant="body2" sx={{ mt: 1, mb: 1.5 }}>
            Higher efficiency can justify a higher quality score and thus a
            higher price.

          </Typography>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            3.5 Time Decay Penalty
          </Typography>
          <Typography variant="body2">
            Older credits may be discounted using a time-decay factor, depending
            on regulatory and market preferences.
          </Typography>
        </Box>

        <Divider />

        {/* 4. RULES FOR CREDIT RETIREMENT */}
        <Box>
          <Typography variant="h5" gutterBottom>
            4. RULES FOR CREDIT RETIREMENT
          </Typography>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            4.1 Retirement is Final
          </Typography>
          <List dense sx={{ mb: 1.5 }}>
            <ListItem>
              <ListItemText primary="Retirement permanently removes credits from circulation." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Retired credits cannot be un-retired or reused." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Retirement must be visible in the registry." />
            </ListItem>
          </List>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            4.2 Retirement Triggers
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Retirement can be initiated by:
          </Typography>
          <List dense sx={{ mb: 1.5 }}>
            <ListItem>
              <ListItemText primary="user or organisation request" />
            </ListItem>
            <ListItem>
              <ListItemText primary="regulatory or compliance obligations" />
            </ListItem>
            <ListItem>
              <ListItemText primary="automatic triggers when a certificate is used as proof-of-offset" />
            </ListItem>
          </List>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            4.3 Retirement Record Requirements
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            A retired credit must store:
          </Typography>
          <List dense sx={{ mb: 1.5 }}>
            <ListItem>
              <ListItemText primary="reason for retirement" />
            </ListItem>
            <ListItem>
              <ListItemText primary="timestamp" />
            </ListItem>
            <ListItem>
              <ListItemText primary="retiring entity (wallet, organisation, or user)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="link back to device and energy provenance" />
            </ListItem>
            <ListItem>
              <ListItemText primary="audit hash or transaction reference" />
            </ListItem>
          </List>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            4.4 NFT Retirement Model
          </Typography>
          <Typography variant="body2">
            For NFT-based credits:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="retirement can be implemented as a burn or a non-reversible “retired” flag" />
            </ListItem>
            <ListItem>
              <ListItemText primary="metadata and history must remain queryable after retirement" />
            </ListItem>
            <ListItem>
              <ListItemText primary="on-chain proof-of-retirement events should be emitted" />
            </ListItem>
          </List>
        </Box>

        <Divider />

        {/* 5. RULES FOR CORRELATION WITH ENERGY & CO2 */}
        <Box>
          <Typography variant="h5" gutterBottom>
            5. RULES FOR CORRELATION WITH ENERGY PRODUCTION &amp; CO₂ REDUCTION
          </Typography>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            5.1 Device Energy → CO₂ Avoided
          </Typography>
          <CodeBlock>
            {`CO₂e_Avoided = EnergyProduced_kWh × EmissionFactor`}
          </CodeBlock>
          <Typography variant="body2" sx={{ mt: 1, mb: 1.5 }}>
            EmissionFactor is the CO₂ intensity of the displaced grid
            electricity, usually defined per region by official datasets.

          </Typography>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            5.2 Dynamic Regional Intensity
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Different grids have different baseline emissions:
          </Typography>
          <List dense sx={{ mb: 1.5 }}>
            <ListItem>
              <ListItemText primary="coal-heavy grids: typically 0.7 – 1.1 kg CO₂/kWh" />
            </ListItem>
            <ListItem>
              <ListItemText primary="high-renewable grids: typically 0.1 – 0.3 kg CO₂/kWh" />
            </ListItem>
          </List>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            5.3 Statistical Smoothing
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Emission factors may be smoothed using hourly averages, rolling 24h
            factors, or seasonal curves to avoid overreacting to short-term
            volatility.
          </Typography>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            5.4 Device Integrity Requirement
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Energy measurement must originate from:
          </Typography>
          <List dense sx={{ mb: 1.5 }}>
            <ListItem>
              <ListItemText primary="certified meters (e.g. NMI, IEC-compliant)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="audited IoT logs" />
            </ListItem>
            <ListItem>
              <ListItemText primary="cryptographically signed measurement packets (for blockchain-integrated devices)" />
            </ListItem>
          </List>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            5.5 Zero Double Counting Rule
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Energy used to generate:
          </Typography>
          <List dense sx={{ mb: 1 }}>
            <ListItem>
              <ListItemText primary="LGCs / RECs" />
            </ListItem>
            <ListItem>
              <ListItemText primary="carbon credits" />
            </ListItem>
            <ListItem>
              <ListItemText primary="specific PPAs with environmental claims" />
            </ListItem>
          </List>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            …cannot be double-counted across multiple instruments. Only one
            environmental product is allowed per unit of energy within the
            declared boundary.

          </Typography>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            5.6 Traceability Chain
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Maintain a full trace from production to retirement:
          </Typography>
          <CodeBlock>
            {`Energy → Token → Credit → Retirement → Audit Hash`}
          </CodeBlock>
          <Typography variant="body2" sx={{ mt: 1 }}>
            This ensures strong auditability, anti-fraud controls, and regulator
            alignment.

          </Typography>
        </Box>

        <Divider />

        {/* 6. OPTIONAL ADVANCED RULES (for blockchain platforms) */}
        <Box>
          <Typography variant="h5" gutterBottom>
            6. OPTIONAL ADVANCED RULES (for blockchain platforms)
          </Typography>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            6.1 Zero-Knowledge Proofs
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Zero-knowledge proofs (ZKPs) can be used to verify:
          </Typography>
          <List dense sx={{ mb: 1.5 }}>
            <ListItem>
              <ListItemText primary="device authenticity without exposing private keys" />
            </ListItem>
            <ListItem>
              <ListItemText primary="measurement correctness while hiding raw data" />
            </ListItem>
            <ListItem>
              <ListItemText primary="cross-chain or off-chain computations (e.g. aggregation) without revealing sensitive details" />
            </ListItem>
          </List>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            6.2 Device-Origin Signature Model
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Each credit can embed:
          </Typography>
          <List dense sx={{ mb: 1.5 }}>
            <ListItem>
              <ListItemText primary="device wallet or platform master wallet" />
            </ListItem>
            <ListItem>
              <ListItemText primary="signature over the measurement hash" />
            </ListItem>
            <ListItem>
              <ListItemText primary="proof-of-origin binding credits to specific physical devices" />
            </ListItem>
          </List>

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            6.3 Freeze–Burn Model for Minting
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            To prevent double-spend in tokenised systems:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="freeze the underlying Carbon/Ergon tokens before minting an NFT credit" />
            </ListItem>
            <ListItem>
              <ListItemText primary="once minting succeeds, burn or permanently lock those tokens" />
            </ListItem>
            <ListItem>
              <ListItemText primary="record the linkage so one unit of energy → one set of tokens → one credit → one retirement path only" />
            </ListItem>
          </List>
        </Box>
      </Stack>
    </Container>
  );
};

export default CarbonCreditRulesDoc;
